import { langId } from "@escapio/shared/l10n/lang-id";
import { translate } from "@escapio/shared/l10n/translate";

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

const RELATIVE_FORMAT_THRESHOLDS = {
	year: YEAR,
	month: MONTH,
	week: WEEK,
	day: DAY,
	hour: HOUR,
	minute: MINUTE,
	second: SECOND,
};

// Mapping of letters to their meanings for date formatting.
const MAPPING = {
	d: date => date.getDate(),
	M: date => date.getMonth() + 1,
	y: date => date.getFullYear(),
};

// Formats of ending a date range in different locales.
const RANGE_FULL_FORMATS = {
	de: "dd.MM.y",
	en: "MM/dd/y",
	es: "dd/MM/y",
	da: "dd.MM.y",
};

// Formats of starting a date range in different locales. The array means,
// which parts of the date have to be the same for that format to be used.
const RANGE_START_FORMATS = {
	de: {
		"dd.": [Date.prototype.getFullYear, Date.prototype.getMonth],
		"dd.MM.": [Date.prototype.getFullYear],
		"dd.MM.y": [],
	},
	en: {
		"MM/dd": [Date.prototype.getFullYear],
		"MM/dd/y": [],
	},
	es: {
		dd: [Date.prototype.getFullYear, Date.prototype.getMonth],
		"dd/MM": [Date.prototype.getFullYear],
		"dd/MM/y": [],
	},
	da: {
		"dd.": [Date.prototype.getFullYear, Date.prototype.getMonth],
		"dd.MM.": [Date.prototype.getFullYear],
		"dd.MM.y": [],
	},
};

export const monthTranslations = {
	0: translate("basic_month_1"),
	1: translate("basic_month_2"),
	2: translate("basic_month_3"),
	3: translate("basic_month_4"),
	4: translate("basic_month_5"),
	5: translate("basic_month_6"),
	6: translate("basic_month_7"),
	7: translate("basic_month_8"),
	8: translate("basic_month_9"),
	9: translate("basic_month_10"),
	10: translate("basic_month_11"),
	11: translate("basic_month_12"),
};

export const weekDayTranslations = {
	0: translate("basic_weekday_short_0"),
	1: translate("basic_weekday_short_1"),
	2: translate("basic_weekday_short_2"),
	3: translate("basic_weekday_short_3"),
	4: translate("basic_weekday_short_4"),
	5: translate("basic_weekday_short_5"),
	6: translate("basic_weekday_short_6"),
};

// Regexp used to parse date formats using the mapping above.
const REGEXP = new RegExp(
	Object.keys(MAPPING)
		.map(part => `${part}+`)
		.join("|"),
	"g",
);

// Given a piece of date format, return its formatted value.
const getPart = (part, date) =>
	String(MAPPING[part.slice(0, 1)](date)).padStart(part.length, "0");

// Formats a single date given a format like "dd.MM.yyyy", see mapping above for
// reference / extending.
export const formatDate = (format, date) =>
	format.replace(REGEXP, part => getPart(part, date));

export const formatDateToLocale = date =>
	formatDate(RANGE_FULL_FORMATS[langId], date);

export const formatISODate = date => formatDate("y-MM-dd", date);

// Returns true if all the items of the given array are the same, false
// otherwise.
const allSame = ([first, ...rest]) =>
	rest.length > 0 ? rest.every(item => item === first) : true;

const getStartFormat = (start, end) =>
	Object.entries(RANGE_START_FORMATS[langId]).find(([, test]) =>
		test
			.map(fn => [start, end].map(fn.call.bind(fn)))
			.map(allSame)
			.every(val => val),
	)[0];

// Formats a date range according to the current locale.
export const formatRangeToLocale = (start, end) =>
	[
		end
			? formatDate(getStartFormat(start, end), start)
			: formatDateToLocale(start),
		end ? formatDateToLocale(end) : "",
	].join(" – ");

export const formatRangeToDE = (start, end) =>
	[start, end].map(date => formatDate("dd.MM.yyyy", date)).join("-");

/**
 * @param {Date} date
 * @param {number} amount
 * @returns {Date}
 */
export function addDays(date, amount) {
	let newDate = new Date(+date);
	newDate.setDate(date.getDate() + amount);
	return newDate;
}

/**
 * @param {Date} date
 * @param {Number} amount
 * @returns {Date}
 */
export function addMonth(date, amount) {
	let newDate = new Date(+date);
	newDate.setMonth(date.getMonth() + amount);
	return newDate;
}

/**
 * @param {Date} date
 * @param {Number} amount
 * @returns {Date}
 */
export function addYear(date, amount) {
	let newDate = new Date(+date);
	newDate.setFullYear(date.getFullYear() + amount);
	return newDate;
}

export function isBefore(first, second) {
	return compareDates(first, second) === -1;
}

export function isSameDay(first, second) {
	return compareDates(first, second) === 0;
}

export function isAfter(first, second) {
	return compareDates(first, second) === 1;
}

export const compareDates = (a, b) => {
	const first = new Date(+a);
	const second = new Date(+b);
	first.setHours(0, 0, 0, 0);
	second.setHours(0, 0, 0, 0);

	if (first > second) {
		return 1;
	}
	if (first < second) {
		return -1;
	}
	return 0;
};

export const firstDayOfWeek = langId === "en" ? 0 : 1;

/**
 * Get the days of a week in locale specific order
 *
 * @param {Number} firstDayOfWeek optional default is 0 for Sunday
 * @return {array}
 */
export function getWeekDays(firstDayOfWeek = 0) {
	const weekDays = [];
	for (let day = 0; day < 7; day++) {
		weekDays.push((firstDayOfWeek + day) % 7);
	}
	return weekDays;
}

/**
 * @param {Date} date1
 * @param {Date} date2
 * @returns {Number}
 */
export const getNumNightsBetween = (date1, date2) => {
	date1.setHours(0, 0, 0, 0);
	date2.setHours(0, 0, 0, 0);
	// We need to round the date, so we get always a whole number. This is necessary due to day light saving.
	return Math.round(
		(date2.getTime() - date1.getTime()) / (24 * 60 * 60 * 1000),
	);
};

/**
 * @param {Date} date1
 * @param {Date} date2
 * @returns {Number}
 */
export const getNumMonthsContaining = (date1, date2) => {
	return (
		date2.getMonth() -
		date1.getMonth() +
		12 * (date2.getFullYear() - date1.getFullYear()) +
		1
	);
};

/**@param {Intl.RelativeTimeFormat} intlFormatter
 * @param {Date} date
 * @param {Date} referenceDate defaults to now
 * @return {string}
 */
export const getRelativeTime = (
	intlFormatter,
	date,
	referenceDate = new Date(),
) => {
	const delta = date - referenceDate;
	return Object.entries(RELATIVE_FORMAT_THRESHOLDS).reduce(
		(prev, [unit, threshold]) => {
			return (
				prev ||
				(Math.abs(delta) > threshold || unit === "second"
					? intlFormatter.format(Math.round(delta / threshold), unit)
					: null)
			);
		},
		null,
	);
};

/**
 * @param {Date} start
 * @param {Date} end
 * @param {Boolean=true} includeEnd
 * @yields {Date}
 */
export function* dateIterator(start, end, includeEnd = true) {
	let current = start;
	do {
		yield current;
		current = addDays(current, 1);
	} while (includeEnd ? !isAfter(current, end) : isBefore(current, end));
}
